import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Check } from 'lucide-react';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import type { PlayerStatus } from '@swiss-magic/shared-types';

export function StatusToggle({
  status,
  onChange,
  testIdPrefix,
  showBadge = false,
}: {
  status: PlayerStatus;
  onChange: (s: PlayerStatus) => void | Promise<void>;
  testIdPrefix: string;
  showBadge?: boolean;
}) {
  const [pendingAction, setPendingAction] = useState<'accepted' | 'rejected' | null>(null);

  async function handleClick(action: 'accepted' | 'rejected') {
    const targetStatus = status === action ? 'none' : action;
    setPendingAction(action);
    try {
      await onChange(targetStatus);
    } finally {
      setPendingAction(null);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }

  function btnClass(action: 'accepted' | 'rejected') {
    if (pendingAction === action) return 'bg-amber-500 hover:bg-amber-600 text-white';
    if (status === action && action === 'accepted') return 'bg-green-600 hover:bg-green-700';
    if (status === action && action === 'rejected') return 'bg-red-600 hover:bg-red-700';
    return '';
  }

  function btnVariant(action: 'accepted' | 'rejected') {
    if (pendingAction === action || status === action) return 'default' as const;
    return 'outline' as const;
  }

  return (
    <div data-testid={`${testIdPrefix}-status`} className="flex items-center gap-1">
      <Button
        size="sm"
        variant={btnVariant('rejected')}
        className={`h-7 w-7 p-0 ${btnClass('rejected')}`}
        data-testid={`${testIdPrefix}-status-rejected`}
        onClick={(e) => { e.stopPropagation(); handleClick('rejected'); }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant={btnVariant('accepted')}
        className={`h-7 w-7 p-0 ${btnClass('accepted')}`}
        data-testid={`${testIdPrefix}-status-accepted`}
        onClick={(e) => { e.stopPropagation(); handleClick('accepted'); }}
      >
        <Check className="h-3.5 w-3.5" />
      </Button>
      {showBadge && (
        <span className="ml-1 w-20 text-xs" data-testid={`${testIdPrefix}-status-badge`}>
          <PlayerStatusBadge status={status} />
        </span>
      )}
    </div>
  );
}
